/**
 * canvas.ts — the on-screen DOM render + drag layer (spec §1–§3).
 *
 * Ports the geometry/placement/dangle proven in dev/ring-harness.html, but
 * driven by the REAL modules: geometry.ts for positioning, state.ts for the
 * build/pricing, data.ts for the catalog. Charms are absolutely-positioned DOM
 * elements transformed with GSAP (free hit-testing + GPU-composited drag); no
 * <canvas> for interaction (that's only the PNG exporter, later).
 *
 * Desktop layout only for now: design canvas on the left, charm library on the
 * right, running total bar across the bottom. Mobile/step-nav come later and are
 * layout-only because positions are stored as `t`.
 */

import gsap from 'gsap';
import { CustomEase } from 'gsap/CustomEase';

import {
  tToXY, tToHangXY, tToHangRotation, projectToRing, distToRing,
  nudgeToFreeSlot, isSlotFree, SIZE_CM, REF_PIECE_CM,
  type RingParams, type CharmSize,
} from './geometry';
import {
  addCharm, placeCharm, parkCharm, removeCharm,
  setChain, setClosure, setLength, lengthBounds, defaultLength,
  isAtMax, ringCount, maxCharms, runningTotal, countCharm,
  BASE_LAYOUT,
  type BuildState, type BaseKind,
} from './state';
import { makeLoadingButton } from './loadingButton';
import {
  beadsLayoutFor, containBox, slotPx, slotAngleDeg, nearestFreeSlot,
  type SlotBox, type BeadSlot,
} from './slots';
import { toCharmProduct, closureImage, closuresForChain, toClosureSelection, type Catalog, type CharmCatalogItem } from './data';
import { mountPanel, type PanelApi, type TabName } from './ui';
import { mountFilters } from './filters';
import { mountScrollbar } from './vscroll';
import { buildStepIndicator } from './steps';
import { buildReviewBackdrop, buildReviewHeader, buildReviewDecor, buildOrderOverview } from './step3';
import { addBuildToCart, prepareSnapshot, type PreparedSnapshot } from './cart';

export interface ConfiguratorOpts {
  /** navigate back to step 1 (base picker) — wired from the flow (index.ts). Step 3
   *  (review) is handled in-place inside the canvas (enterReview), not a separate mount. */
  onBack?: () => void;
  /** Save / update the current build as a favorite — owned by the flow (index.ts),
   *  which handles the snapshot, dedup, toast and dirty-baseline reset. Fired by both
   *  the bottom-bar SAVE button and the review screen's "Save in favorites". */
  onSave?: () => void | Promise<void>;
  /** Fired after the review screen's Add to bag completes, so the flow can stop the
   *  leave-guard nagging about saving a design that's already in the cart. */
  onAddedToBag?: () => void;
  /** When editing a piece opened FROM the cart: the old cart group is removed on add, so
   *  the CTA becomes "Update piece in bag" and re-adding replaces it (no duplicate). */
  updateCartGroupId?: string | null;
}

gsap.registerPlugin(CustomEase);
CustomEase.create('charmLand', 'M0,0 C0.78,0 0.22,1 1,1');

// ─── Tunables (ported from the harness) ──────────────────────────────────────

// Global charm scale: drawn diameter ÷ real footprint. 1.0 = footprint-accurate
// (a charm's pixels equal its spacing footprint, so neighbours sit shoulder-to-
// shoulder). Bump >1 to make charms read larger than their footprint. Tune this
// together with SIZE_CM and REF_PIECE_CM (geometry.ts) against the debug ring.
// Per-base charm/closure size multipliers come from catalog.sizes (section ranges); see
// DEFAULT_SIZES in data.ts for the baked-in defaults (bracelet 1, necklace 0.66, etc.).
// Per-size spacing tightness: multiplies the reserved arc half-width only (NOT the
// drawn size). <1 pulls neighbours closer. XL charm art carries more transparent
// padding, so its 6cm footprint over-reserves arc — tighten it so XL charms sit closer.
const SPACING_SCALE: Record<CharmSize, number> = { XS: 1, S: 1, M: 1, L: 1, XL: 0.65 };
const DEBUG_RING      = false; // draw the geometry RingParams circle on top of the PNG to calibrate the asset
const BEADS_SHOW_SLOTS = false; // TEMP: always show the beads charm-slot circles for calibration reference
const BEADS_CALIBRATE  = false; // TEMP: drag the numbered dots to calibrate slot positions, then "Copy slots"
const BEADS_CALIBRATE_CLOSURES = false; // TEMP: drag the carabiner handle(s) to place the closures, then "Copy closures"
const SNAP_TOLERANCE  = 110;   // px from the ring arc to snap on
const DRAG_THRESHOLD  = 8;     // px of movement before a press becomes a drag
const RING_IMG_SCALE  = 1.1;   // ring PNG box ÷ ring diameter — calibrate to the real asset
const CLOSURE_SCALE   = 0.16;   // closure PNG width ÷ ring diameter — calibrate to the real asset
const CLOSURE_OFFSET  = 0.006;  // nudge closure down off the t=0 anchor (× ring radius); + = down
const WIPE_MS         = 500;    // review sidebar clip-path wipe duration (must match panel/overview `duration-500`)
// Mobile Chains tab: when the drawer is expanded on the Chains tab, zoom into the top of
// the ring so the chain + closure read large. CHAIN_ZOOM_VW = target ring width as a
// fraction of viewport width (1.10 ≈ 110vw). CHAIN_ZOOM_BIAS = where the closure centre
// sits within the visible canvas (the band between the top bar and the expanded drawer),
// as a fraction of that band: 0 = top, 0.5 = middle. Being a fraction (not fixed px) keeps
// the framing consistent across screen heights (SE vs 14) instead of drifting up on taller
// phones where the extra height would otherwise all fall below the cluster.
const CHAIN_ZOOM_VW   = 1.15;
const CHAIN_ZOOM_BIAS = .6;
// Beads (bag charm) mobile Chain-tab zoom: like the ring zoom but framed on the LEFT
// carabiner. REGION = fraction of the base width the zoom targets (smaller = closer);
// X/Y = where the closure lands as a fraction of the visible band.
const BEADS_ZOOM_REGION = 0.8;   // larger = less zoomed (more of the piece in view)
const BEADS_ZOOM_X      = 0.44;  // closure x as a fraction of the stage width
const BEADS_ZOOM_Y      = 0.72;  // closure y as a fraction of the visible band (higher = lower on screen)
// Mobile Charms tab: how far the piece lifts when the drawer is expanded (%). Less
// negative = ring sits lower. Just enough to clear the sheet while keeping it in view.
const CHARMS_LIFT     = -7;
const FADE_MS         = 200;   // ring/closure cross-fade; matches their `transition-opacity duration-200`
const SWAP_SETTLE_MS  = 50;    // min pause AFTER swapping the src (while invisible) before fading in
// Parked charms render smaller (grow to full size when dragged out). Mobile a touch
// larger so they're tappable; desktop smaller. Resolved per-mount in mountConfigurator.
const SVGNS = 'http://www.w3.org/2000/svg';
// Px of the mobile bottom-sheet visible when collapsed (handle + tabs + a sliver). Also
// reserved at the bottom of the stage when centring the ring so it doesn't tuck behind it.
const DRAWER_PEEK = 100;

const REDUCE = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const mobileMql = () => window.matchMedia('(max-width: 767px)').matches;
// Window listeners are scoped to the current mount via this controller; re-mounting
// (e.g. crossing the mobile/desktop breakpoint) aborts the previous mount's listeners.
let cfgAbort: AbortController | null = null;
const ROT_GAIN = 0.7, ROT_MAX = 22, SNAP_KICK_GAIN = 1.2, MIN_KICK = 9;
const clampDeg = (d: number) => Math.max(-ROT_MAX, Math.min(ROT_MAX, d));

const money = (n: number) => '€' + n.toFixed(2);
const priceLabel = (n: number) => '€' + (Number.isInteger(n) ? n : n.toFixed(2));
const initials = (t: string) => (t || '?').split(/\s+/).map(w => w[0]).join('').slice(0, 2).toUpperCase();
const el = (tag: string, cls?: string) => { const e = document.createElement(tag); if (cls) e.className = cls; return e; };

// Per-charm hover tint, picked deterministically from 3 brand options (same idea as
// product cards: id % 3). Light blue / yellow / pink. Applied via mix-blend-color.
const HOVER_COLORS = ['#801431', '#C8D7EA', '#FFCA22'];
const hoverColor = (id: string): string => {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return HOVER_COLORS[h % HOVER_COLORS.length];
};
const DROPLET = `<svg viewBox="0 0 24 24" width="11" height="11" fill="currentColor"><path d="M12 2.5c3.5 4.2 6 7.5 6 10.5a6 6 0 1 1-12 0c0-3 2.5-6.3 6-10.5z"/></svg>`;
// Sparkle for the "Original" (PaoPao Original) badge.
const SPARKLE = `<svg viewBox="0 0 24 24" width="10" height="10" fill="currentColor"><path d="M12 2l2.2 6.2L20.5 10l-5.3 2.1L12 18l-2.2-5.9L4.5 10l5.3-1.8L12 2z"/></svg>`;
// strokes use currentColor so the icon follows the button's text color (dark → white on hover).
const TRASH = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
  <path d="M13.5 3.5H2.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M5.5 1.5H10.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M12.5 3.5V13C12.5 13.1326 12.4473 13.2598 12.3536 13.3536C12.2598 13.4473 12.1326 13.5 12 13.5H4C3.86739 13.5 3.74021 13.4473 3.64645 13.3536C3.55268 13.2598 3.5 13.1326 3.5 13V3.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`;

interface Instance {
  uid: string;
  el: HTMLElement;
  orient: HTMLElement;
  body: HTMLElement;
  size: CharmSize;
  d: number;
}

interface DragState {
  inst: Instance;
  fromT: number | null;
  startX: number; startY: number;
  lastX: number;  vx: number;
  moved: boolean;
  rotTo: ((v: number) => void) | null;
  /** Beads only: smoothly chase the slot orientation (so a flip animates, not jumps). */
  orientTo: ((deg: number) => void) | null;
}

/**
 * Mount the interactive configurator into `root`, replacing whatever is there.
 * All mutable state lives in this closure.
 */
export function mountConfigurator(root: HTMLElement, catalog: Catalog, build: BuildState, opts: ConfiguratorOpts = {}): void {
  root.innerHTML = '';

  // Mobile gets the app-shell layout: top bar, ring fills, charms/chains in a bottom
  // drawer; charms are tapped (not dragged) into the tray. Re-read each mount so a
  // breakpoint-crossing re-mount picks up the new value.
  const MOBILE = mobileMql();
  const TRAY_SCALE = MOBILE ? 0.86 : 0.72; // mobile tray charms a bit larger (tappable)
  // Canvas renderer: 'ring' (bracelet/necklace) or 'beads' (bag charm — a fixed base
  // image with charms at hard-pinned slots, see slots.ts). All ring-specific geometry
  // below branches on BEADS; the ring path is unchanged.
  const LAYOUT = BASE_LAYOUT[build.base];
  const BEADS = LAYOUT === 'beads';
  // Per-base beads geometry (charm slots, carabiner anchors, orientation focal). For ring
  // bases this resolves to a default but is never read (every use is BEADS-guarded).
  const beadsCfg = beadsLayoutFor(build.base);
  // Scope this mount's window listeners; abort the previous mount's.
  cfgAbort?.abort();
  cfgAbort = new AbortController();
  const signal = cfgAbort.signal;

  // ── DOM scaffold ──────────────────────────────────────────────────────────
  // Layout via Tailwind; the bespoke canvas geometry (ring/charms) keeps its
  // scoped cfg__* classes since the JS drives those with inline transforms.
  // Mobile: full-height app shell (flex column, bar pinned on top via pt clearance,
  // drawer overlays the bottom). Desktop: the original two-column grid.
  const wrap     = el('div', 'cfg relative bg-[#F5F5F5] flex flex-col h-[100svh] overflow-hidden px-3 pt-20 pb-0 md:h-auto md:overflow-visible md:grid md:grid-cols-[1fr_480px] md:grid-rows-[1fr_auto] md:px-4 md:pt-18 md:pb-16');
  const stage    = el('div', 'cfg__stage relative touch-none flex-1 min-h-0 md:z-10 md:flex-none md:min-h-[480px] md:col-start-1 md:row-start-1');
  // Resolve the chain from the (persisted) build so returning to this step keeps the
  // user's selection; fall back to the first catalog chain on a fresh entry.
  const chain    = (build.chain ? catalog.chains.find(c => c.id === build.chain!.id) : null) ?? catalog.chains[0] ?? null;
  const sizes    = catalog.sizes; // merchant-tunable charm/closure size multipliers
  // Always create the ring img so ui.ts can swap its src when the chain changes.
  // z-1: chain sits BELOW the charms (z-10) and the closure (z-20).
  const ringImg  = Object.assign(new Image(), { className: 'cfg__ring z-1 transition-opacity duration-200 ease-out', alt: '' });
  if (chain?.image) ringImg.src = chain.image;
  // Closure composited at the top anchor (t=0): a single PNG holding both clasp halves,
  // pulled from the `closure` metaobject and swapped when the closure selection changes.
  const closureImg = Object.assign(new Image(), { className: 'cfg__closure z-20 transition-opacity duration-200 ease-out', alt: '' });
  // Beads bases composite a carabiner per closure anchor (bag charm = 2, keychain = 1); the
  // ring uses only `closureImg`. closureImgs[i] maps to beadsCfg.closures[i]; extras are hidden.
  const closureImg2 = Object.assign(new Image(), { className: 'cfg__closure z-20 transition-opacity duration-200 ease-out', alt: '' });
  const closureImgs = [closureImg, closureImg2];
  const blueDot  = el('div', 'cfg__blue-dot');
  // Parking tray: a bottom-anchored, horizontally-scrollable pill. Parked charms live
  // INSIDE the track as flow children (so it scrolls natively with a scrollbar); they
  // re-parent to the stage while dragged/placed (parkToTray / freeToStage). A trash
  // drop-zone sits fixed on the right; a fade hints when there's more to scroll.
  const tray       = el('div', 'cfg__tray absolute bottom-0 left-1/2 -translate-x-1/2 flex items-center gap-2 w-max max-w-[360px] rounded-sm bg-detail/10 px-2 py-1.5 opacity-0 transition-opacity duration-200 ease-out');
  const trayArea   = el('div', 'relative flex-1 min-w-0');
  const trayScroll = el('div', 'cfg__tray-scroll overflow-x-auto no-scrollbar overflow-y-hidden pt-3'); // pt gives the guidance bounce room (not clipped)
  const trayTrack  = el('div', 'flex items-center gap-2 w-max');
  const trayFade   = el('div', 'absolute right-0 inset-y-0 w-1/5 z-999 pointer-events-none bg-gradient-to-r from-transparent to-[#ececec] opacity-0 transition-opacity duration-200 ease-out');
  const trayFadeL  = el('div', 'absolute left-0 inset-y-0 w-1/5 z-999 pointer-events-none bg-gradient-to-l from-transparent to-[#ececec] opacity-0 transition-opacity duration-200 ease-out');
  trayScroll.appendChild(trayTrack);
  trayArea.append(trayScroll, trayFade, trayFadeL);
  const trayTrash  = el('div', 'cfg__tray-trash shrink-0 flex items-center justify-center w-14 h-14 text-foreground pointer-events-none');
  trayTrash.innerHTML = TRASH;
  tray.append(trayArea, trayTrash);
  // Guidance shown while the ring is empty: a hint above the tray + a soft bounce on the
  // first parked charm (see layoutTray). Only the `p` type class styles the text.
  const trayHint = el('p', 'cfg__tray-hint p absolute left-1/2 -translate-x-1/2 text-center opacity-0 transition-opacity duration-200 ease-out pointer-events-none');
  trayHint.textContent = 'Drag charms onto chain';

  // Stage header (title / subtitle) — overlaid top-left. The waterproof note now lives
  // below the chain grid in the panel (ui.ts).
  const header = el('div', 'absolute top-0 left-0 z-[7] max-w-[40ch] pointer-events-none transition-opacity duration-300 ease-out');
  // h4 on mobile, h3 on desktop. `.h3`/`.h4` are base classes (no responsive
  // variants), and they're different fonts entirely, so render both and toggle.
  header.innerHTML =
    `<h2 class="h4 md:hidden m-0 mb-2">Design your ${build.base.replace(/_/g, ' ')}</h2>` +
    `<h2 class="h3 hidden md:block m-0 md:mb-3">Design your ${build.base.replace(/_/g, ' ')}</h2>`;
  // Subtitle copy is state-driven (chain step → charm guidance → max reached); see updateHeader.
  const headerSub = el('p', 'p m-0 mb-2 w-[35ch] max-w-full transition-opacity duration-200 ease-out');
  header.appendChild(headerSub);

  // Piece layer groups the ring + closure + charms so review mode can scale them as one
  // (charms are appended here too — see makeInstance / freeToStage).
  const pieceLayer = el('div', 'cfg__piece absolute inset-0 pointer-events-none transition-transform duration-500 ease-out');
  pieceLayer.append(ringImg, closureImg, closureImg2, blueDot);
  // Beads base image is sized/positioned to the (scaled) base box in layoutBeadsBase;
  // slots + charms map to that same box.
  if (BEADS) {
    ringImg.classList.remove('z-1');
    Object.assign(ringImg.style, { position: 'absolute', objectFit: 'contain', zIndex: '1' });
  }
  stage.append(header, tray, trayHint, pieceLayer);

  // Debug overlay: the exact geometry circle charms attach to (calibration aid).
  // Static reference circles — off while calibrating (the draggable handles replace them).
  const debug = (DEBUG_RING || (BEADS && BEADS_SHOW_SLOTS && !BEADS_CALIBRATE)) ? document.createElementNS(SVGNS, 'svg') : null;
  let layoutCalibration: () => void = () => {}; // assigned by mountCalibration() (beads calibrate mode)
  if (debug) { debug.setAttribute('class', 'cfg__debug'); stage.appendChild(debug); }

  // Right column owned by ui.ts (Chains | Charms tabs). The charm library grid is
  // built here (drag-spawn lives in canvas) and handed to the panel's Charms tab.
  // Mobile: a bottom drawer (sheet) that drags up to ~60svh; desktop: the right column.
  const panel   = el('aside', 'z-40 absolute inset-x-0 bottom-0 h-[55svh] bg-background rounded-t-sm shadow-[0_-6px_24px_rgba(0,0,0,0.10)] overflow-hidden flex flex-col transition-transform duration-300 ease-out md:relative md:z-10 md:inset-auto md:h-full md:w-full md:bg-transparent md:rounded-none md:shadow-none md:overflow-visible md:flex-row md:items-start md:col-start-2 md:row-start-1 md:transition-[clip-path] md:duration-500');
  // Charm library: a top bar (heading + filter button, injected below) over the grid.
  const library = el('div', 'cfg__library flex flex-col min-h-0 h-full px-3 pt-3 md:p-4 transition-opacity');
  const libBar  = el('div', 'flex items-center justify-between gap-3 mb-3 md:mb-[18px] shrink-0');
  const libHeading = el('h3', 'h4 md:h3 m-0'); libHeading.textContent = 'CHARMS LIBRARY';
  // The grid scrolls inside this wrapper; a custom vertical scrollbar (like reviews /
  // saved designs) shows how far down you are — needed once there are 100+ charms.
  const libScrollWrap = el('div', 'relative flex-1 min-h-0');
  const libGrid = el('div', 'h-full overflow-y-auto no-scrollbar pr-2 pb-4');
  libScrollWrap.append(libGrid);
  libBar.appendChild(libHeading);
  library.append(libBar, libScrollWrap);
  const updateLibScroll = mountScrollbar(libGrid, libScrollWrap, 'y');
  const bar     = el('div', 'absolute z-50 inset-x-3 top-3 md:inset-x-4 md:top-auto md:bottom-4 md:left-4 md:w-[calc(100vw-2rem)] flex items-center justify-between gap-3 md:gap-4 px-3 py-2.5 md:py-2 rounded-sm bg-foreground text-background text-xs transition-[transform,opacity] duration-500 ease-out');

  // ── Review-mode chrome (step 3) — built hidden, animated in by enterReview() ──
  // The piece itself is the LIVE one above; review only morphs the surrounding chrome.
  const reviewBg   = buildReviewBackdrop(catalog);                 // blue + image, fades in
  const baseLabel  = catalog.bases.find(b => b.kind === build.base)?.label ?? build.base;
  const reviewHead = buildReviewHeader(baseLabel, () => exitReview()); // "YOUR CUSTOM …" + Edit design
  const reviewDecor = buildReviewDecor();                         // yellow paopao, fades in
  stage.append(reviewHead, reviewDecor);
  // Order-overview lives in the right column, over the panel; clip-path wipe in/out.
  const overviewSlot = el('aside', 'absolute inset-0 z-30 w-full h-full transition-[clip-path] duration-500 ease-out pointer-events-none md:static md:col-start-2 md:row-start-1 md:h-[calc(100%+3rem)]');
  overviewSlot.style.clipPath = 'inset(0 0 0 100%)'; // hidden (collapsed to the right; reveals right→left)
  // Idle clip extends past the left edge so the panel's left-overflowing tabs aren't clipped;
  // the review wipe animates left-inset → 100% (out to the right). Desktop only — the mobile
  // drawer is positioned by transform, not clipped.
  if (!MOBILE) panel.style.clipPath = 'inset(0 0 0 -120px)';

  wrap.append(reviewBg, stage, panel, overviewSlot, bar);
  // Step indicator (bottom-left, above the action bar). Slides down with the bar in review.
  const stepsEl = el('div', 'hidden md:block absolute left-3 md:left-4 bottom-16 z-50');
  stepsEl.style.transition = 'transform 450ms ease'; // slides down in review; label morph is the stepper's own
  const stepper = buildStepIndicator(2, n => {
    if (n === 1) { opts.onBack?.(); return; }   // back to base picker (unmounts the canvas)
    if (n === 2 && reviewMode) exitReview();
    if (n === 3 && !reviewMode) enterReview();
  });
  stepsEl.appendChild(stepper.el);
  wrap.appendChild(stepsEl);
  root.appendChild(wrap);

  // ── Mutable state ─────────────────────────────────────────────────────────
  const instances = new Map<string, Instance>();
  let ring: RingParams = computeRing();
  let drag: DragState | null = null;
  let panelApi: PanelApi;  // assigned once the panel (ui.ts) is mounted, below
  let currentTab: TabName = BEADS ? 'beads' : 'chains'; // drives header copy + bar CTA (overwritten by onTab)
  let charmsVisited = false;          // once the Charms tab is opened, the bar CTA → "Continue to review"
  let reviewMode = false;             // step-3 review morph is active (in-place, see enterReview)
  let snapshotSig = '';               // signature of the last snapshotted build (re-upload only on change)
  let snapshotJob: Promise<PreparedSnapshot> | null = null;
  let ringSrc = chain?.image ?? '';   // currently shown chain image (for fade-on-change)
  let ringGen = 0;                    // generation guard for the ring cross-fade (drops stale swaps)
  let visibleCharms = catalog.charms; // library list after filters (default: all)
  // Mobile drawer (set up in setupMobileDrawer); no-ops on desktop.
  let collapseDrawer: () => void = () => {};
  let drawerExpanded = false;
  let drawerSetPeek: (px: number) => void = () => {};
  let drawerSnap: (toExpanded: boolean) => void = () => {};
  let drawerAttachDrag: (zone: HTMLElement | null) => void = () => {}; // make a zone raise/lower the sheet
  let drawerApplyRingZoom: () => void = () => {}; // reposition/zoom the piece for the current drawer + tab (mobile)
  let mobileOrder: HTMLElement | null = null; // review order overview inside the drawer

  function computeRing(): RingParams {
    const w = stage.clientWidth || 600;
    const h = stage.clientHeight || 680;
    const radius = Math.min(w, h) * (MOBILE ? 0.288 : 0.24); // mobile ring 20% larger
    // Horizontally centered. On mobile, centre the ring in the VISIBLE canvas — the
    // stage runs to the viewport bottom, but the collapsed drawer covers the bottom
    // `DRAWER_PEEK` px, so we centre in (h - DRAWER_PEEK) to keep it from tucking
    // behind the sheet. The ring lifts further when the drawer expands (see snap()).
    const cy = MOBILE ? (h - DRAWER_PEEK) / 2 : h * 0.46;
    return { cx: w / 2, cy, radius };
  }
  const restingDeg = (t: number) => tToHangRotation(t) * 180 / Math.PI;
  const tOf = (uid: string) => build.charms.find(c => c.uid === uid)?.t ?? null;

  // ── Beads (bag charm) geometry — see slots.ts. `t` is a slot INDEX, not an angle. ──
  // Charm footprint vs the base-image width (section range × a built-in per-base factor —
  // keychain charms read ~2× the bag charm's regardless of the section %).
  const BEADS_CHARM_SCALE = sizes.charm[build.base] * (build.base === 'keychain' ? 2 : 1);
  // Overall beads-piece size (mobile ~10% larger). The keychain renders 50% smaller on mobile.
  const BEADS_BASE_SCALE = (MOBILE ? 0.77 : 0.70) * (MOBILE && build.base === 'keychain' ? 0.5 : 1);
  // Push the whole beads piece DOWN (fraction of box height). Per-base on desktop: the bag
  // charm sits lower; the keychain sits a bit higher. Mobile shares one (sits a bit higher).
  const BEADS_BASE_OFFSET_Y = MOBILE ? -0.06 : (build.base === 'keychain' ? 0.05 : 0.13);
  const SLOT_SNAP_TOL = MOBILE ? 70 : 88;        // px from a dot to accept a drop
  // Rendered box of the base bead image inside the stage: object-contain, then scaled down
  // by BEADS_BASE_SCALE and re-centred. Everything (image, slots, charms, closures) maps to
  // this box, so shrinking it shrinks the whole bagchain together.
  const baseBox = (): SlotBox => {
    const full = containBox(stage.clientWidth || 600, stage.clientHeight || 680, ringImg.naturalWidth || 4, ringImg.naturalHeight || 3);
    const w = full.width * BEADS_BASE_SCALE;
    const h = full.height * BEADS_BASE_SCALE;
    return { left: full.left + (full.width - w) / 2, top: full.top + (full.height - h) / 2 + h * BEADS_BASE_OFFSET_Y, width: w, height: h };
  };
  // Slot indices already taken by other placed charms (beads spacing = one charm/slot).
  const occupiedSlots = (excludeUid: string) =>
    build.charms.filter(c => c.t !== null && c.uid !== excludeUid).map(c => c.t as number);

  // Anchor (where the charm's top sits) + orientation for a placed position `t`:
  //   ring  → point on the ring at angle t, hanging radially outward;
  //   beads → the pinned slot at index t, top pointing toward the chain.
  const anchorXY = (t: number) => BEADS ? slotPx(beadsCfg.slots[t], baseBox()) : tToXY(t, ring);
  const orientForT = (t: number) => BEADS ? slotAngleDeg(beadsCfg.slots[t], beadsCfg.focal) : restingDeg(t);
  // outer-wrapper left/top so the rotation pivot (body top-center) sits on the anchor
  const hangCenter = (t: number, d: number) => { const a = anchorXY(t); return { x: a.x, y: a.y + d / 2 }; };
  // On-screen diameter = real footprint (size_cm/REF_PIECE_CM) × scale. Ring scales with the
  // ring circumference; beads scale with the base-image width.
  const charmPx = (size: CharmSize) => BEADS
    ? (SIZE_CM[size] / REF_PIECE_CM) * baseBox().width * BEADS_CHARM_SCALE
    : (SIZE_CM[size] / REF_PIECE_CM) * (2 * Math.PI * ring.radius) * sizes.charm[build.base];
  // Spacing half-width (t-units) derived from the ACTUAL rendered size: the
  // charm's pixel radius over the circumference of the circle its body sits on
  // (ring.radius + radius, since charms hang outward). Measuring on that outer
  // circle is what makes two charms' visible edges nearly touch — using the bare
  // ring radius over-reserves arc and leaves a gap that grows with size.
  const halfWidthOf = (size: CharmSize) => {
    const r = charmPx(size) / 2;
    return (r / (2 * Math.PI * (ring.radius + r))) * SPACING_SCALE[size];
  };
  // Occupied set for the geometry spacing functions: position + rendered half-width.
  const occupied = (excludeUid: string) =>
    build.charms.filter(c => c.t !== null && c.uid !== excludeUid)
      .map(c => ({ t: c.t as number, hw: halfWidthOf(c.size) }));

  // ── Charm instances ───────────────────────────────────────────────────────
  function makeInstance(uid: string, item: CharmCatalogItem): Instance {
    const size = item.attributes.size;
    const d = charmPx(size);

    const node = el('div', 'cfg__charm group pointer-events-auto');
    node.style.width = d + 'px';
    node.style.height = d + 'px';
    node.dataset.uid = uid;

    const orient = el('div', 'cfg__orient');
    const body = el('div', 'cfg__body');
    if (item.image) {
      const img = Object.assign(new Image(), { src: item.image, alt: item.title });
      img.onerror = () => { img.remove(); body.textContent = initials(item.title); };
      body.appendChild(img);
    } else {
      body.textContent = initials(item.title);
      body.style.fontSize = Math.round(d * 0.4) + 'px';
    }
    orient.appendChild(body);
    node.appendChild(orient);
    if (!REDUCE) gsap.set(body, { transformOrigin: '50% 0%' });

    pieceLayer.appendChild(node);
    const inst: Instance = { uid, el: node, orient, body, size, d };
    instances.set(uid, inst);

    // Hover affordance: show the blue dot behind a charm that's ON the ring (not
    // while dragging — pointermove drives the orb then, and tray charms are skipped).
    node.addEventListener('pointerenter', () => {
      if (drag || reviewMode) return;
      const t = tOf(uid);
      if (t === null) return;
      // Orb sits behind the charm: at its slot (beads) or on the ring outward point (ring).
      showOrb(BEADS ? anchorXY(t) : tToHangXY(t, ring, inst.d / 2), inst.d);
    });
    node.addEventListener('pointerleave', () => { if (!drag) hideOrb(); });

    // Hover trash: a glass button at the charm's OUTWARD TIP. It lives inside `orient`
    // (the transformed containing block) so it follows the charm's hang angle — for a
    // side charm it sits on the side edge — while setOrient counter-rotates it so the
    // icon stays upright. It's a descendant of the charm so hovering it keeps the charm
    // hovered (group-hover, no dead-zone); pointerdown stops propagation so pressing it
    // never starts a drag. Only the icon deletes — a plain charm click just drags.
    const trash = el('div', 'cfg__trash hidden absolute left-1/2 top-full pt-1 z-[3] opacity-0 pointer-events-none transition-opacity duration-200 ease-out group-hover:opacity-100 group-hover:pointer-events-auto');
    trash.style.transform = 'translateX(-50%)';
    const trashBtn = el('span', 'flex items-center justify-center w-8 h-8 rounded-xs bg-white text-foreground shadow-sm cursor-pointer transition-colors duration-200 ease-out hover:bg-foreground hover:text-background');
    trashBtn.innerHTML = TRASH;
    trash.appendChild(trashBtn);
    trash.addEventListener('pointerdown', e => e.stopPropagation()); // don't begin a drag
    trashBtn.addEventListener('click', e => {
      e.stopPropagation();
      if (reviewMode) return; // frozen during review
      removeInstance(uid);
      hideOrb();
      layoutTray();
      renderBar();
    });
    orient.appendChild(trash);

    return inst;
  }

  function removeInstance(uid: string): void {
    removeCharm(build, uid);
    instances.get(uid)?.el.remove();
    instances.delete(uid);
    renderLibrary(); // a unit of this charm is back in stock → re-enable its chip if capped
  }

  // Show/hide the hover-trash for a charm (hidden only while it's being dragged; it's
  // available on both ring and tray charms). On mobile there's no hover, so it's always
  // hidden — charms are deleted by dragging them onto the tray trash instead.
  const setTrashShown = (inst: Instance, on: boolean) =>
    inst.el.querySelector('.cfg__trash')?.classList.toggle('hidden', MOBILE ? true : !on);

  // Rotate a charm's body to `deg` and counter-rotate its trash so the icon stays
  // upright while the button sits at the charm's (rotated) outward tip.
  const setOrient = (inst: Instance, deg: number): void => {
    inst.orient.style.transform = `rotate(${deg}deg)`;
    const tb = inst.el.querySelector('.cfg__trash') as HTMLElement | null;
    if (tb) tb.style.transform = `translateX(-50%) rotate(${-deg}deg)`;
  };

  // Position + orient a just-placed charm at `t` (ring angle OR beads slot index), with the
  // landing tween. Shared by the ring and beads commit paths.
  function placeAtT(inst: Instance, t: number, vx: number): void {
    const c = hangCenter(t, inst.d);
    setOrient(inst, orientForT(t));
    if (REDUCE) {
      inst.el.style.left = c.x + 'px'; inst.el.style.top = c.y + 'px';
      inst.el.style.width = inst.d + 'px'; inst.el.style.height = inst.d + 'px';
    } else {
      gsap.to(inst.el, { left: c.x, top: c.y, width: inst.d, height: inst.d, duration: 0.28, ease: 'charmLand' });
      settle(inst.body, vx);
    }
  }

  // ── Layout ────────────────────────────────────────────────────────────────
  function layoutRing(): void {
    ring = computeRing();
    if (BEADS) {
      layoutBeadsBase();
    } else {
      if (ringImg) {
        const s = ring.radius * 2 * RING_IMG_SCALE;
        Object.assign(ringImg.style, { width: s + 'px', height: s + 'px', left: (ring.cx - s / 2) + 'px', top: (ring.cy - s / 2) + 'px' });
      }
      const a = tToXY(0, ring); // closure anchor at t=0 (top-center)
      const cw = ring.radius * 2 * (build.base === 'keychain' ? sizes.closure.keychain : CLOSURE_SCALE);
      closureImg.style.width = cw + 'px';   // height auto-derives from the PNG aspect
      closureImg.style.left = a.x + 'px';
      closureImg.style.top = (a.y + ring.radius * CLOSURE_OFFSET) + 'px'; // tiny downward nudge; .cfg__closure centers via translate(-50%,-50%)

      if (debug) {
        const w = stage.clientWidth, h = stage.clientHeight;
        debug.setAttribute('width', String(w));
        debug.setAttribute('height', String(h));
        debug.setAttribute('viewBox', `0 0 ${w} ${h}`);
        debug.innerHTML =
          `<circle cx="${ring.cx}" cy="${ring.cy}" r="${ring.radius}" fill="none" stroke="#ff3b3b" stroke-width="1.5"/>` +
          `<circle cx="${ring.cx}" cy="${ring.cy}" r="3" fill="#ff3b3b"/>` +              // center
          `<circle cx="${a.x}" cy="${a.y}" r="5" fill="none" stroke="#ff3b3b" stroke-width="1.5"/>`; // t=0 anchor
      }
    }

    // Refresh charm pixel sizes (footprint scales with the ring) and reposition the
    // ring charms. Tray charms are flow-positioned inside the track, so only their
    // (scaled) size is refreshed here.
    for (const inst of instances.values()) {
      inst.d = charmPx(inst.size);
      const t = tOf(inst.uid);
      if (t !== null) {
        inst.el.style.width = inst.d + 'px';
        inst.el.style.height = inst.d + 'px';
        const c = hangCenter(t, inst.d);
        inst.el.style.left = c.x + 'px';
        inst.el.style.top = c.y + 'px';
        setOrient(inst, orientForT(t));
        setTrashShown(inst, true);  // placed charm → enable its hover-trash (un-gate the `hidden`)
      } else if (inst.el.classList.contains('in-tray')) {
        const sd = inst.d * TRAY_SCALE;
        inst.el.style.width = sd + 'px';
        inst.el.style.height = sd + 'px';
        setTrashShown(inst, false); // tray charm → no per-charm trash (deletes via the tray trash)
      }
    }
    layoutTray();
  }

  // Beads base layout: the bead image fills the stage (object-contain via CSS); place the
  // two carabiner closures at the chain's end slots and, when calibrating (DEBUG_RING),
  // draw a numbered overlay of every pinned slot.
  function layoutBeadsBase(): void {
    const box = baseBox();
    // Render the base bead image at the (scaled) box.
    Object.assign(ringImg.style, { left: box.left + 'px', top: box.top + 'px', width: box.width + 'px', height: box.height + 'px' });
    const cw = box.width * sizes.closure[build.base === 'keychain' ? 'keychain' : 'bag_charm']; // carabiner width vs base image (section range)
    const ends = beadsCfg.closures; // dedicated carabiner anchors for this base (bag charm = 2, keychain = 1)
    closureImgs.forEach((im, i) => {
      const slot = ends[i];
      if (!slot) { im.style.display = 'none'; return; } // base has fewer closures than image slots
      im.style.display = '';
      const p = slotPx(slot, box);
      im.style.width = cw + 'px';
      im.style.left = p.x + 'px';
      im.style.top = p.y + 'px';
      im.style.zIndex = '0'; // beads: carabiner sits BEHIND the chain (below ringImg z-1)
      // Attach the carabiner by its TOP-CENTRE to the dot (not its centre) and rotate around it.
      im.style.transformOrigin = '50% 0';
      im.style.transform = `translate(-50%, 0) rotate(${slot.angle ?? 0}deg)`;
    });
    if (debug) {
      const w = stage.clientWidth, h = stage.clientHeight;
      debug.setAttribute('width', String(w));
      debug.setAttribute('height', String(h));
      debug.setAttribute('viewBox', `0 0 ${w} ${h}`);
      debug.innerHTML = beadsCfg.slots.map((s, i) => {
        const p = slotPx(s, box);
        return `<circle cx="${p.x}" cy="${p.y}" r="9" fill="rgba(255,202,34,0.85)" stroke="#801431" stroke-width="1.5"/>` +
               `<text x="${p.x}" y="${p.y + 4}" fill="#801431" font-size="10" font-weight="700" text-anchor="middle">${i}</text>`;
      }).join('');
    }
    layoutCalibration(); // reposition the drag handles (no-op unless calibrate mode)
  }

  // ── Drag-to-calibrate (TEMP) ────────────────────────────────────────────────
  // Numbered handles you drag onto the artwork; the copy button emits the named array
  // (normalised, preserving flip/angle) to the clipboard + console. Reused for the charm
  // slots (BAGCHAIN_SLOTS) and the carabiner closures (BAGCHAIN_CLOSURE_SLOTS). Off in prod.
  function mountCalibration(slots: BeadSlot[], exportName: string, btnLabel: string, btnPos: string, tint: string): void {
    const calSlots: BeadSlot[] = slots.map(s => ({ ...s }));
    const layer = el('div', 'absolute inset-0 z-[60] pointer-events-none');
    const handles: HTMLElement[] = [];

    calSlots.forEach((_s, i) => {
      const h = el('div', `absolute -translate-x-1/2 -translate-y-1/2 w-5 h-5 rounded-full border-2 ${tint} text-[10px] font-bold flex items-center justify-center cursor-grab pointer-events-auto select-none touch-none`);
      h.textContent = String(i);
      let on = false;
      h.addEventListener('pointerdown', e => {
        e.preventDefault(); e.stopPropagation();
        on = true; h.setPointerCapture(e.pointerId); h.style.cursor = 'grabbing';
      });
      h.addEventListener('pointermove', e => {
        if (!on) return;
        const r = stage.getBoundingClientRect();
        const x = e.clientX - r.left, y = e.clientY - r.top;
        h.style.left = x + 'px'; h.style.top = y + 'px';
        const box = baseBox();
        calSlots[i].x = (x - box.left) / box.width;
        calSlots[i].y = (y - box.top) / box.height;
      });
      const up = (): void => { on = false; h.style.cursor = 'grab'; };
      h.addEventListener('pointerup', up);
      h.addEventListener('pointercancel', up);
      handles.push(h);
      layer.appendChild(h);
    });

    const copyBtn = el('button', `absolute ${btnPos} z-[61] pointer-events-auto bg-[#801431] text-white text-xs font-semibold px-3 py-2 rounded-xs cursor-pointer`) as HTMLButtonElement;
    copyBtn.type = 'button';
    copyBtn.textContent = btnLabel;
    copyBtn.addEventListener('click', () => {
      const out = `export const ${exportName}: BeadSlot[] = [\n` +
        calSlots.map((s, i) =>
          `  /* ${i} */ { x: ${s.x.toFixed(4)}, y: ${s.y.toFixed(4)}${s.flip ? ', flip: true' : ''}${s.angle != null ? ', angle: ' + s.angle : ''}${s.nudge != null ? ', nudge: ' + s.nudge : ''} },`
        ).join('\n') + '\n];';
      console.log(out);
      navigator.clipboard?.writeText(out).catch(() => {});
      copyBtn.textContent = 'Copied! (also in console)';
      window.setTimeout(() => { copyBtn.textContent = btnLabel; }, 1500);
    });
    layer.appendChild(copyBtn);
    stage.appendChild(layer);

    const prevLayout = layoutCalibration; // chain so multiple calibrators can coexist
    layoutCalibration = (): void => {
      prevLayout();
      const box = baseBox();
      handles.forEach((h, i) => {
        const p = slotPx(calSlots[i], box);
        h.style.left = p.x + 'px';
        h.style.top = p.y + 'px';
      });
    };
  }

  // Move a charm INTO the tray track (flow child, scaled, upright). Idempotent — a
  // charm already in the track isn't re-appended (keeps its order).
  function parkToTray(inst: Instance): void {
    gsap.killTweensOf(inst.el);
    inst.el.style.left = '';
    inst.el.style.top = '';
    inst.el.style.zIndex = '';
    const sd = inst.d * TRAY_SCALE;
    inst.el.style.width = sd + 'px';
    inst.el.style.height = sd + 'px';
    inst.orient.style.transform = 'rotate(0deg)';
    inst.el.classList.add('in-tray');
    if (inst.el.parentElement !== trayTrack) trayTrack.appendChild(inst.el);
  }

  // Lift a charm OUT of the tray onto the stage (absolute, free) so it can be dragged
  // or placed. The caller positions/sizes it afterwards.
  function freeToStage(inst: Instance): void {
    inst.el.classList.remove('in-tray');
    if (inst.el.parentElement !== pieceLayer) pieceLayer.appendChild(inst.el);
  }

  // Fade hint on the right edge of the scroll viewport when there's more to scroll.
  function updateFade(): void {
    const more = trayScroll.scrollWidth - trayScroll.clientWidth - trayScroll.scrollLeft > 1;
    trayFade.style.opacity = more ? '1' : '0';
    trayFadeL.style.opacity = trayScroll.scrollLeft > 1 ? '1' : '0'; // fade the left edge too
  }
  trayScroll.addEventListener('scroll', updateFade);
  // Custom horizontal scrollbar so people know the tray scrolls to more charms.
  const updateTrayScroll = mountScrollbar(trayScroll, trayArea, 'x');

  function layoutTray(): void {
    if (reviewMode) { tray.style.opacity = '0'; return; } // no tray during review
    // Count parked charms in STATE (includes one being dragged) so the tray + trash stay
    // visible mid-drag; the track itself only holds the resting parked charms.
    const parkedCount = [...instances.values()].filter(i => tOf(i.uid) === null).length;
    // Show the tray when anything is parked; on mobile also keep it (for its trash) as
    // long as ANY charm exists on the canvas (ring or tray). Hide it only when empty.
    tray.style.opacity = (parkedCount > 0 || (MOBILE && build.charms.length > 0)) ? '1' : '0';
    // When nothing is parked, drop the scroll area so the lone trash stays centred.
    trayArea.style.display = parkedCount > 0 ? '' : 'none';
    if (parkedCount === 0) { trayHint.style.opacity = '0'; updateFade(); updateTrayScroll(); return; }

    // Only the FIRST parked charm bounces, and only while the ring is empty.
    const guide = ringCount(build) === 0;
    const parked = [...trayTrack.children] as HTMLElement[];
    parked.forEach((node, i) => {
      (node.querySelector('.cfg__orient') as HTMLElement | null)?.classList.toggle('cfg__bounce', guide && i === 0);
    });

    trayHint.style.opacity = guide ? '1' : '0';
    trayHint.style.bottom = tray.offsetHeight + 4 + 'px'; // 0.25rem above the tray
    updateFade();
    updateTrayScroll();
  }

  // ── Bottom bar ──────────────────────────────────────────────────────────
  // Skeleton built once (so button handlers survive); renderBar only updates the
  // live count/total text. Left: actions · Center: status · Right: CTA.
  function buildBar(): void {
    const barBtn = 'inline-flex items-center gap-1.5 border-0 bg-transparent text-white h4 cursor-pointer hover:opacity-80';
    bar.innerHTML =
      `<div class="flex gap-2 justify-self-start" data-bar-left>` +
        `<button type="button" class="${barBtn}" data-act="restart"><span class="w-5" aria-hidden="true"><svg class="w-5 h-5" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
  <path d="M14.25 8.00001C14.2501 9.6432 13.6032 11.2204 12.4491 12.3901C11.2951 13.5598 9.7268 14.228 8.08375 14.25H8C6.40442 14.2534 4.86872 13.6426 3.71125 12.5444C3.56662 12.4078 3.48218 12.2194 3.4765 12.0205C3.47081 11.8217 3.54435 11.6287 3.68094 11.4841C3.81752 11.3394 4.00597 11.255 4.20482 11.2493C4.40366 11.2436 4.59662 11.3172 4.74125 11.4538C5.42015 12.0947 6.27289 12.5214 7.1929 12.6804C8.11291 12.8395 9.05939 12.7238 9.91407 12.348C10.7687 11.9722 11.4937 11.3528 11.9984 10.5673C12.503 9.78181 12.765 8.86498 12.7516 7.93142C12.7381 6.99786 12.4499 6.08897 11.9228 5.31832C11.3957 4.54766 10.6532 3.94943 9.78804 3.59838C8.92289 3.24733 7.97347 3.15903 7.05843 3.34451C6.14338 3.52999 5.30328 3.98103 4.64312 4.64126C4.635 4.64938 4.6275 4.65688 4.61875 4.66438L3.43062 5.75001H4.5C4.69891 5.75001 4.88968 5.82903 5.03033 5.96968C5.17098 6.11033 5.25 6.3011 5.25 6.50001C5.25 6.69892 5.17098 6.88969 5.03033 7.03034C4.88968 7.17099 4.69891 7.25001 4.5 7.25001H1.5C1.30109 7.25001 1.11032 7.17099 0.96967 7.03034C0.829018 6.88969 0.75 6.69892 0.75 6.50001V3.50001C0.75 3.3011 0.829018 3.11033 0.96967 2.96968C1.11032 2.82903 1.30109 2.75001 1.5 2.75001C1.69891 2.75001 1.88968 2.82903 2.03033 2.96968C2.17098 3.11033 2.25 3.3011 2.25 3.50001V4.79501L3.5925 3.56626C4.46806 2.6954 5.58198 2.10344 6.79367 1.86509C8.00536 1.62673 9.2605 1.75267 10.4007 2.22701C11.5409 2.70135 12.515 3.50282 13.2 4.53027C13.8851 5.55773 14.2505 6.7651 14.25 8.00001Z" fill="white"/>
</svg></span><span class="hidden md:inline">Restart</span></button>` +
      `</div>` +
      `<div class="justify-self-center text-white/80 whitespace-nowrap text-xs flex flex-col items-center leading-tight md:block md:leading-normal"><span class="cfg__bar-count"></span><span class="mx-2 opacity-50 hidden md:inline">·</span><span class="cfg__bar-total"></span></div>` +
      `<button type="button" class="justify-self-end inline-flex items-center gap-2 border-0 bg-transparent text-white h4 cursor-pointer" data-act="continue"><span class="cfg__cta-label">Continue to charms</span> <span class="text-base leading-none" aria-hidden="true"><svg class="w-4" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
  <path d="M14.0306 8.53061L9.53062 13.0306C9.38973 13.1715 9.19863 13.2507 8.99937 13.2507C8.80012 13.2507 8.60902 13.1715 8.46812 13.0306C8.32723 12.8897 8.24807 12.6986 8.24807 12.4994C8.24807 12.3001 8.32723 12.109 8.46812 11.9681L11.6875 8.74999H2.5C2.30109 8.74999 2.11032 8.67097 1.96967 8.53032C1.82902 8.38966 1.75 8.1989 1.75 7.99999C1.75 7.80107 1.82902 7.61031 1.96967 7.46966C2.11032 7.329 2.30109 7.24999 2.5 7.24999H11.6875L8.46937 4.02999C8.32848 3.88909 8.24932 3.69799 8.24932 3.49874C8.24932 3.29948 8.32848 3.10838 8.46937 2.96749C8.61027 2.82659 8.80137 2.74744 9.00062 2.74744C9.19988 2.74744 9.39098 2.82659 9.53187 2.96749L14.0319 7.46749C14.1018 7.53726 14.1573 7.62016 14.1951 7.71142C14.2329 7.80269 14.2523 7.90052 14.2522 7.99931C14.252 8.09809 14.2324 8.19588 14.1944 8.28706C14.1564 8.37824 14.1007 8.46101 14.0306 8.53061Z" fill="white"/>
</svg></span></button>`;
    bar.querySelector('[data-act="restart"]')!.addEventListener('click', restart);
    // Save button: same loading wipe as the review screen ("Save" → "Saving" → back), greyed
    // (opacity) while the save + upload runs, and re-clicks ignored. Icon-first like Restart.
    const saveIcon = `<svg class="w-5 h-5" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M11.125 1.75C9.86937 1.75 8.755 2.24563 8 3.0975C7.245 2.24563 6.13062 1.75 4.875 1.75C3.78139 1.75132 2.73295 2.18635 1.95965 2.95965C1.18635 3.73295 0.751323 4.78139 0.75 5.875C0.75 10.3962 7.36312 14.0087 7.64437 14.1606C7.75367 14.2195 7.87586 14.2503 8 14.2503C8.12414 14.2503 8.24633 14.2195 8.35562 14.1606C8.63687 14.0087 15.25 10.3962 15.25 5.875C15.2487 4.78139 14.8137 3.73295 14.0404 2.95965C13.2671 2.18635 12.2186 1.75132 11.125 1.75ZM10.7819 10.6475C9.91142 11.3861 8.9809 12.0509 8 12.635C7.01909 12.0509 6.08858 11.3861 5.21812 10.6475C3.86375 9.48562 2.25 7.71375 2.25 5.875C2.25 5.17881 2.52656 4.51113 3.01884 4.01884C3.51113 3.52656 4.17881 3.25 4.875 3.25C5.9875 3.25 6.91875 3.8375 7.30562 4.78375C7.36193 4.92169 7.45805 5.03974 7.58172 5.12283C7.70539 5.20592 7.85101 5.2503 8 5.2503C8.14899 5.2503 8.29461 5.20592 8.41828 5.12283C8.54195 5.03974 8.63807 4.92169 8.69437 4.78375C9.08125 3.8375 10.0125 3.25 11.125 3.25C11.8212 3.25 12.4889 3.52656 12.9812 4.01884C13.4734 4.51113 13.75 5.17881 13.75 5.875C13.75 7.71375 12.1362 9.48562 10.7819 10.6475Z" fill="white"/></svg>`;
    const barSave = makeLoadingButton(
      'Save', barBtn, '', () => opts.onSave?.(), 'Saving', saveIcon,
      { disabledPalette: ['opacity-70'], labelClass: 'hidden! md:block!', iconFirst: true },
    );
    bar.querySelector('[data-bar-left]')!.appendChild(barSave);
    // Before visiting the Charms tab: jump to it. After: advance to review (works even
    // with zero charms — some pieces are ordered bare).
    bar.querySelector('[data-act="continue"]')!.addEventListener('click', () => {
      if (charmsVisited) enterReview();
      else panelApi.switchTab('charms');
    });
  }

  // State-driven left-hand guidance copy (chain step → charm guidance → max reached).
  // Only re-renders (with a fade-out → fade-in) when the copy actually changes.
  let headerHtml = '';
  let headerFade: number | undefined;
  function updateHeader(): void {
    let next: string;
    if (currentTab !== 'charms') { // chain step (ring: chains; beads: beads/chain)
      next = 'Pick your favorite chain to continue. You can still switch chains after you added charms.';
    } else {
      const cnt = ringCount(build), mx = maxCharms(build), left = mx - cnt;
      if (cnt === 0) next = 'Click a charm in the library to add it to your tray.<br>Drag to move it onto your design.';
      else if (left <= 0) next = 'Maximum amount of charms added. Remove a charm first if you want to add other ones.';
      else next = `You can add ${left} more charm${left === 1 ? '' : 's'}.<br>${MOBILE ? 'Drag charm onto trash icon to remove.' : 'Click on the trash icon to remove a charm.'}`;
    }
    if (next === headerHtml) return;
    const first = headerHtml === '';
    headerHtml = next;
    clearTimeout(headerFade);
    if (REDUCE || first) { headerSub.innerHTML = next; headerSub.style.opacity = '1'; return; }
    headerSub.style.opacity = '0';
    headerFade = window.setTimeout(() => {
      headerSub.innerHTML = next;
      requestAnimationFrame(() => { headerSub.style.opacity = '1'; });
    }, 200);
  }

  function renderBar(): void {
    const cnt = ringCount(build), mx = maxCharms(build);
    const atMax = cnt >= mx;
    library.classList.toggle('opacity-50', atMax);
    library.classList.toggle('pointer-events-none', atMax);
    const countEl = bar.querySelector('.cfg__bar-count');
    const totalEl = bar.querySelector('.cfg__bar-total');
    if (countEl) {
      countEl.innerHTML = `<strong>${cnt} / ${mx}</strong> charms attached${atMax ? ' · MAX' : ''}`;
      countEl.classList.toggle('text-[#f0a04b]', atMax);
    }
    if (totalEl) totalEl.innerHTML = `Running total <strong class="font-bold text-white">${money(runningTotal(build))}</strong>`;
    const ctaLabel = bar.querySelector('.cfg__cta-label');
    if (ctaLabel) ctaLabel.textContent = charmsVisited
      ? (MOBILE ? 'Review' : 'Continue to review')
      : (MOBILE ? 'Charms' : 'Continue to charms');
    updateHeader();
  }

  // ── Step 3 (review) — in-place morph ────────────────────────────────────────
  // Morph step 2 → step 3 in place: the live piece stays, only the chrome animates.
  function enterReview(): void {
    if (reviewMode) return;
    reviewMode = true; // freezes drag/trash/hover via the guards in those handlers
    pieceLayer.classList.add('is-review'); // CSS hides the hover-trash on charms
    hideOrb();
    tray.style.opacity = '0';
    trayHint.style.opacity = '0';
    // Fresh order overview (the build may have changed since last entry).
    // Pre-generate + upload the snapshot now (piece is frozen in review) so it's ready by
    // the time they click Add to bag. Re-upload only when the piece actually changed since
    // the last review entry (edit → back → review re-captures; unchanged re-entry reuses).
    const sig = JSON.stringify({
      chain: build.chain?.id ?? null,
      closure: build.closure?.variantId ?? null,
      length: build.length,
      charms: build.charms.filter(c => c.t !== null)
        .map(c => `${c.productId}@${(c.t as number).toFixed(3)}`).sort(),
    });
    if (sig !== snapshotSig || !snapshotJob) {
      snapshotSig = sig;
      snapshotJob = prepareSnapshot(catalog, build);
    }
    const onAddToBag = async () => {
      await addBuildToCart(catalog, build, snapshotJob ? await snapshotJob : undefined, opts.updateCartGroupId);
      snapshotSig = ''; snapshotJob = null; // build id rotated → next piece re-captures
      opts.onAddedToBag?.();                // already in the cart → don't nag to save on leave
    };
    const onSaveFavorite = () => opts.onSave?.();
    const bagLabel = opts.updateCartGroupId ? 'Update piece in bag' : undefined;

    // Common chrome: blue backdrop + decor + header cross-fade.
    reviewBg.style.opacity = '1';
    reviewDecor.style.opacity = '1';
    header.style.opacity = '0';
    reviewHead.style.opacity = '1';
    reviewHead.style.pointerEvents = 'auto';

    if (MOBILE) {
      // The top bar fades out; the "YOUR CUSTOM …" header rides up into its place.
      bar.style.opacity = '0'; bar.style.pointerEvents = 'none';
      reviewHead.style.transform = `translateY(-${bar.offsetHeight + 8}px)`;
      // Yellow paopao decor: bottom-right, ~8% off the edge, around the closed-drawer top.
      // zIndex 1 lifts it above the blue backdrop (which is z-0 isolate).
      Object.assign(reviewDecor.style, { left: 'auto', right: '-14%', bottom: '15%', width: '52vw', transform: 'rotate(-12deg)', zIndex: '1' });
      // Piece scales up 10% and lifts a bit (the drawer's closed → there's room up top).
      pieceLayer.style.transformOrigin = `${ring.cx}px ${ring.cy}px`;
      pieceLayer.style.transition = 'transform 300ms ease';
      pieceLayer.style.transform = 'translateY(-11%) scale(1.1)'; // base ring already higher on mobile
      pieceLayer.style.zIndex = '2'; // above the decor (z-1), below the drawer (z-40)
      // Drawer content: fade the design out, the order overview in.
      panel.querySelectorAll<HTMLElement>('.cfg__panel-design').forEach(e => { e.style.opacity = '0'; e.style.pointerEvents = 'none'; });
      mobileOrder?.remove();
      mobileOrder = buildOrderOverview(catalog, build, { mobile: true, onAddToBag, onSaveFavorite, bagLabel });
      mobileOrder.style.cssText = 'position:absolute;left:0;right:0;top:34px;bottom:0;opacity:0;transition:opacity 300ms ease';
      panel.appendChild(mobileOrder);
      requestAnimationFrame(() => { if (mobileOrder) mobileOrder.style.opacity = '1'; });
      // Collapse the drawer to a peek that shows the order's pinned top block, and make
      // that block a drag zone too (like the tabs strip in step 2) so the sheet is easy
      // to pull up — the grabber alone is a tiny target.
      requestAnimationFrame(() => {
        const topB = mobileOrder?.querySelector('.cfg__order-top') as HTMLElement | null;
        if (topB) { topB.style.touchAction = 'none'; drawerAttachDrag(topB); }
        drawerSetPeek((topB?.offsetHeight ?? 200) + 46);
      });
      return;
    }

    overviewSlot.innerHTML = '';
    overviewSlot.appendChild(buildOrderOverview(catalog, build, { onAddToBag, onSaveFavorite, bagLabel }));
    // Chains/charms selector wipes fully out to the right; then (after WIPE_MS + 30ms)
    // the order overview wipes in from right→left.
    panel.style.clipPath = 'inset(0 0 0 100%)';
    panel.style.pointerEvents = 'none';
    window.setTimeout(() => {
      overviewSlot.style.clipPath = 'inset(0 0 0 0)';
      overviewSlot.style.pointerEvents = 'auto';
    }, WIPE_MS + 30);
    bar.style.transform = 'translateY(160%)';          // black bar drops out of view
    stepsEl.style.transform = 'translateY(48px)';      // stepper slides down (stays in view)
    stepper.setActive(3);                              // labels wipe closed/open + colours transition
    pieceLayer.style.transformOrigin = `${ring.cx}px ${ring.cy}px`;
    pieceLayer.style.transform = 'translateY(4%) scale(1.1)'; // grows 10% + nudges down a touch
  }

  // Reverse the morph back to the editable design.
  function exitReview(): void {
    if (!reviewMode) return;
    reviewMode = false;
    pieceLayer.classList.remove('is-review');
    reviewBg.style.opacity = '0';
    reviewDecor.style.opacity = '0';
    header.style.opacity = '1';
    reviewHead.style.opacity = '0';
    reviewHead.style.pointerEvents = 'none';

    if (MOBILE) {
      reviewHead.style.transform = '';
      bar.style.opacity = ''; bar.style.pointerEvents = '';
      pieceLayer.style.transform = ''; // back to normal scale
      pieceLayer.style.zIndex = '';
      // Fade the order overview out, the design back in; restore the small peek.
      const order = mobileOrder;
      if (order) { order.style.opacity = '0'; window.setTimeout(() => order.remove(), 320); mobileOrder = null; }
      panel.querySelectorAll<HTMLElement>('.cfg__panel-design').forEach(e => { e.style.opacity = ''; e.style.pointerEvents = ''; });
      drawerSetPeek(100);
      drawerSnap(false);
      layoutTray();
      return;
    }
    // Overview wipes fully out to the right; then (after WIPE_MS + 30ms) the selector wipes in.
    overviewSlot.style.clipPath = 'inset(0 0 0 100%)';
    overviewSlot.style.pointerEvents = 'none';
    window.setTimeout(() => {
      panel.style.clipPath = 'inset(0 0 0 -120px)';
      panel.style.pointerEvents = '';
    }, WIPE_MS + 30);
    bar.style.transform = '';
    stepsEl.style.transform = '';
    stepper.setActive(2);
    pieceLayer.style.transform = '';
    layoutTray(); // restore tray visibility based on parked charms
  }

  // Clear the whole build back to defaults (first chain, default length/closure,
  // no charms) and re-render the panel + canvas.
  function restart(): void {
    for (const c of [...build.charms]) removeInstance(c.uid);
    // Reset to THIS base's default chain (not catalog.chains[0], which is a bracelet chain).
    // Mirrors index.ts onChoose: first in-stock chain for the base, then any base chain, then any.
    const chain0 =
      catalog.chains.find(c => c.variants.some(v => v.base === build.base && v.inventory > 0)) ??
      catalog.chains.find(c => c.variants.some(v => v.base === build.base)) ??
      catalog.chains[0] ?? null;
    setChain(build, chain0);
    const cls = closuresForChain(catalog, chain0).find(c => c.available > 0) ?? null;
    setClosure(build, cls ? toClosureSelection(cls) : null);
    setLength(build, defaultLength(build.base, lengthBounds(chain0, build.base)));
    panelApi.refresh();
    refreshChain();
    layoutRing();
    renderBar();
  }

  // Called by the panel (ui.ts) after a chain/length/closure change.
  // Swap the chain image with a fade-out → fade-in (only when it actually changes, so
  // length/closure tweaks don't flicker the ring). The new PNG is preloaded during the
  // fade-out so it's ready when we swap.
  function refreshChain(): void {
    const c = catalog.chains.find(c => c.id === build.chain?.id);
    const src = c?.image ?? '';
    if (src !== ringSrc) {
      ringSrc = src;
      if (REDUCE || !src) { ++ringGen; ringImg.src = src; ringImg.style.opacity = '1'; }
      else crossfadeImage(ringImg, src, ++ringGen, () => ringGen);
    }
    refreshClosure();
    renderBar();
  }

  // Swap the composited closure PNG when the selection changes (fade out → in). Hidden
  // when no closure is selected or the metaobject has no `configurator_image` for it.
  let closureSrc = '';
  let closureGen = 0;                  // generation guard for the closure cross-fade
  function refreshClosure(): void {
    const src = closureImage(catalog, build.closure) ?? '';
    if (src === closureSrc) return;
    closureSrc = src;
    // Beads composites one carabiner per closure anchor (bag charm = 2, keychain = 1); ring uses one.
    const imgs = BEADS ? closureImgs.slice(0, beadsCfg.closures.length) : [closureImg];
    ++closureGen;
    const g = closureGen;
    if (!src) { imgs.forEach(im => { im.style.opacity = '0'; im.removeAttribute('src'); }); return; }
    if (REDUCE) { imgs.forEach(im => { im.src = src; im.style.opacity = '1'; }); return; }
    imgs.forEach(im => crossfadeImage(im, src, g, () => closureGen));
  }

  // ── Library panel ─────────────────────────────────────────────────────────
  function renderLibrary(): void {
    const grid = el('div', 'grid grid-cols-4 gap-2');
    for (const item of visibleCharms) {
      const sold = item.available <= 0;
      // Stock cap: how many of THIS charm are already staged in the build (tray + ring)
      // vs. how many exist. At the cap the chip is disabled like a sold-out one.
      const inBuild = countCharm(build, item.productId);
      const stockReached = !sold && inBuild >= item.available;
      const disabled = sold || stockReached;
      // cfg__lib-item kept as the drag-spawn hook (closest('.cfg__lib-item')). On mobile
      // it's a tap target (no drag-spawn, no touch-none so the drawer can scroll over it).
      const it = el('button',
        `cfg__lib-item group relative aspect-square rounded-sm overflow-hidden bg-[#F5F5F5] ${MOBILE ? 'cursor-pointer' : 'cursor-grab touch-none'}${disabled ? ' opacity-40 cursor-not-allowed' : ''}`) as HTMLButtonElement;
      it.type = 'button';
      it.dataset.productId = item.productId;

      const media = item.image
        ? `<img class="absolute inset-0 w-full h-full object-contain p-3" src="${item.image}" alt="">`
        : `<span class="absolute inset-0 flex items-center justify-center text-foreground/50">${initials(item.title)}</span>`;

      // Corner badges (waterproof + PaoPao Original), stacked bottom-left on both
      // platforms (keeps them clear of the top size/price meta).
      const wpBadge = item.attributes.waterproof
        ? `<span class="inline-flex items-center justify-center w-4.5 h-4.5 rounded bg-foreground text-[#C8D7EA]" aria-hidden="true">${DROPLET}</span>`
        : '';
      const origIcon = catalog.originalBadgeIcon
        ? `<img class="w-2.5 h-2.5 object-contain" src="${catalog.originalBadgeIcon}" alt="">`
        : SPARKLE;
      const origBadge = item.attributes.original
        ? `<span class="inline-flex items-center gap-0.5 h-4.5 px-1 rounded bg-foreground text-background text-[9px] font-semibold uppercase leading-none">${origIcon}Original</span>`
        : '';
      const badges = (wpBadge || origBadge)
        ? `<span class="absolute bottom-1.5 left-1.5 z-[2] flex flex-col items-start gap-1">${origBadge}${wpBadge}</span>`
        : '';

      let belowMedia = '';   // painted UNDER the charm image
      let aboveMedia: string; // painted OVER the charm image
      if (MOBILE) {
        // Size + price shown by default (slightly raised, 70% opacity); sold/none-left state.
        const state = disabled
          ? `<span class="absolute inset-0 z-[3] flex items-center justify-center"><span class="bg-white/80 text-black rounded-xs font-semibold uppercase text-[11px] px-2 py-1">${sold ? 'Sold out' : 'None left'}</span></span>`
          : '';
        aboveMedia =
          `<span class="absolute top-1.5 left-2 z-[2] text-xs font-medium text-foreground/70">${item.attributes.size}</span>` +
          `<span class="absolute top-1.5 right-2 z-[2] text-xs font-medium text-foreground/70">${priceLabel(item.price)}</span>` +
          badges + state;
      } else {
        // Desktop: per-charm hover tint sits BELOW the charm image (tints only the bg
        // showing around it); size/price/Add reveal on hover.
        belowMedia = `<span class="absolute inset-0 mix-blend-color opacity-0 transition-opacity duration-200 ease-out group-hover:opacity-100" style="background-color:${hoverColor(item.productId)}"></span>`;
        aboveMedia = badges +
          `<span class="absolute top-2 left-2 z-[2] text-xs font-medium text-foreground opacity-0 transition-opacity duration-200 ease-out group-hover:opacity-100">${item.attributes.size}</span>` +
          `<span class="absolute top-2 right-2 z-[2] text-xs font-medium text-foreground opacity-0 transition-opacity duration-200 ease-out group-hover:opacity-100">${priceLabel(item.price)}</span>` +
          `<span class="absolute inset-x-1.5 bottom-1.5 z-[2] bg-white/50 text-black backdrop-blur-sm rounded-xs font-semibold uppercase text-xs leading-none flex items-center justify-center py-2 opacity-0 translate-y-1 transition-all duration-200 ease-out group-hover:opacity-100 group-hover:translate-y-0">${sold ? 'Sold out' : stockReached ? 'None left' : 'Add'}</span>`;
      }

      it.innerHTML = belowMedia + media + aboveMedia;
      grid.appendChild(it);
    }
    if (visibleCharms.length === 0) {
      const empty = el('p', 'p text-foreground/50 py-6 text-center col-span-4');
      empty.textContent = 'No charms match these filters.';
      grid.appendChild(empty);
    }
    libGrid.innerHTML = ''; // keep libBar (heading + filter button); only swap the grid
    libGrid.appendChild(grid);
    requestAnimationFrame(updateLibScroll); // size the scrollbar to the new list
  }

  // ── Drag machine (ported from the harness) ────────────────────────────────
  // Orb position is eased via quickTo so when nudgeToFreeSlot snaps the slot to the
  // other side of a charm, the dot glides there instead of hard-cutting. It snaps
  // (no glide) the first time it appears, so it doesn't slide in from a stale spot.
  const orbX = gsap.quickTo(blueDot, 'left', { duration: 0.28, ease: 'power3.out' });
  const orbY = gsap.quickTo(blueDot, 'top',  { duration: 0.28, ease: 'power3.out' });
  let orbVisible = false;
  const showOrb = (c: { x: number; y: number }, d: number) => {
    // Beads: a bit larger (×1.3) since the opaque bead base hides part of it.
    const s = (d + 10) * (BEADS ? 1.3 : 1);
    blueDot.style.width = s + 'px';
    blueDot.style.height = s + 'px';
    if (orbVisible && !REDUCE) { orbX(c.x); orbY(c.y); }      // already showing → glide
    else { orbX(c.x, c.x); orbY(c.y, c.y); }                  // first show → snap into place
    blueDot.style.opacity = '1';
    orbVisible = true;
  };
  const hideOrb = () => { blueDot.style.opacity = '0'; orbVisible = false; };

  function startDrag(inst: Instance, e: PointerEvent): void {
    const r = stage.getBoundingClientRect();
    const sx = e.clientX - r.left, sy = e.clientY - r.top;
    gsap.killTweensOf(inst.el);
    if (!REDUCE) gsap.killTweensOf(inst.body);

    if (MOBILE) { // dragging a charm → drop the drawer so you can design
      collapseDrawer();
      pieceLayer.style.transition = 'none'; pieceLayer.style.transform = ''; // instant: don't offset the drag
    }
    const fromT = tOf(inst.uid);
    if (fromT !== null) parkCharm(build, inst.uid); // detach from ring so it's excluded from spacing
    setTrashShown(inst, false); // hide the trash while dragging
    inst.orient.classList.remove('cfg__bounce'); // stop the guidance bounce when grabbed

    inst.el.setPointerCapture(e.pointerId);
    inst.el.classList.add('is-dragging');
    inst.el.style.zIndex = '100';

    drag = {
      inst, fromT, startX: sx, startY: sy, lastX: sx, vx: 0, moved: false,
      rotTo: REDUCE ? null : gsap.quickTo(inst.body, 'rotation', { duration: 0.3, ease: 'power3.out' }),
      orientTo: (REDUCE || !BEADS) ? null : gsap.quickTo(inst.orient, 'rotation', { duration: 0.35, ease: 'power2.out' }),
    };
  }

  // Spawn a fresh instance from a library item and begin dragging it. If released
  // without moving, it simply lands in the tray (= click-to-add).
  function spawnFromLibrary(item: CharmCatalogItem, e: PointerEvent): void {
    // Sold out, build at max, or already staged every in-stock unit of this charm → no-op.
    if (item.available <= 0 || isAtMax(build) || countCharm(build, item.productId) >= item.available) return;
    const uid = addCharm(build, toCharmProduct(item), null);
    if (!uid) return;
    const inst = makeInstance(uid, item);
    const r = stage.getBoundingClientRect();
    inst.el.style.left = (e.clientX - r.left) + 'px';
    inst.el.style.top = (e.clientY - r.top) + 'px';
    startDrag(inst, e);
    renderLibrary(); // reflect the now-lower remaining stock for this charm
  }

  // Mobile: a tap adds the charm straight to the tray (no drag-from-library); the user
  // then drags it from the tray onto the ring.
  function tapAddFromLibrary(item: CharmCatalogItem): void {
    if (item.available <= 0 || isAtMax(build) || countCharm(build, item.productId) >= item.available) return;
    const uid = addCharm(build, toCharmProduct(item), null);
    if (!uid) return;
    const inst = makeInstance(uid, item);
    parkToTray(inst);
    layoutTray();
    renderBar();
    renderLibrary();
  }

  stage.addEventListener('pointerdown', e => {
    if (reviewMode) return; // piece is frozen during review
    const node = (e.target as HTMLElement).closest('.cfg__charm') as HTMLElement | null;
    if (!node) { if (MOBILE && drawerExpanded) collapseDrawer(); return; } // tap canvas → close drawer
    const inst = instances.get(node.dataset.uid!);
    if (!inst) return;
    e.preventDefault();
    startDrag(inst, e);
  });

  library.addEventListener('pointerdown', e => {
    if (MOBILE) return; // mobile adds on tap (click) so the drawer can still scroll
    const item = (e.target as HTMLElement).closest('.cfg__lib-item') as HTMLElement | null;
    if (!item) return;
    const cat = catalog.charms.find(c => c.productId === item.dataset.productId);
    if (!cat) return;
    e.preventDefault();
    spawnFromLibrary(cat, e);
  });
  if (MOBILE) {
    library.addEventListener('click', e => {
      const item = (e.target as HTMLElement).closest('.cfg__lib-item') as HTMLElement | null;
      if (!item) return;
      const cat = catalog.charms.find(c => c.productId === item.dataset.productId);
      if (cat) tapAddFromLibrary(cat);
    });
  }

  window.addEventListener('pointermove', e => {
    if (!drag) return;
    const r = stage.getBoundingClientRect();
    const lx = e.clientX - r.left, ly = e.clientY - r.top;

    if (!drag.moved) {
      if (Math.hypot(lx - drag.startX, ly - drag.startY) < DRAG_THRESHOLD) return;
      drag.moved = true;
      // Lift it out of the tray onto the stage (if parked) and grow back to full size.
      if (drag.inst.el.classList.contains('in-tray')) freeToStage(drag.inst);
      const d = drag.inst.d;
      if (REDUCE) { drag.inst.el.style.width = d + 'px'; drag.inst.el.style.height = d + 'px'; }
      else gsap.to(drag.inst.el, { width: d, height: d, duration: 0.2, ease: 'charmLand' });
    }
    drag.inst.el.style.left = lx + 'px';
    drag.inst.el.style.top = ly + 'px';

    if (!REDUCE && drag.rotTo) { drag.vx = lx - drag.lastX; drag.rotTo(clampDeg(-drag.vx * ROT_GAIN)); }
    drag.lastX = lx;

    if (BEADS) {
      const slot = nearestFreeSlot(lx, ly, occupiedSlots(drag.inst.uid), baseBox(), SLOT_SNAP_TOL, beadsCfg.slots);
      if (slot !== null) {
        const a = anchorXY(slot);
        showOrb({ x: a.x, y: a.y }, drag.inst.d);
        if (drag.orientTo) drag.orientTo(orientForT(slot));
        else drag.inst.orient.style.transform = `rotate(${orientForT(slot)}deg)`;
      } else {
        hideOrb();
        if (drag.orientTo) drag.orientTo(0);
        else drag.inst.orient.style.transform = 'rotate(0deg)';
      }
    } else if (distToRing(lx, ly, ring) < SNAP_TOLERANCE) {
      const t = nudgeToFreeSlot(projectToRing(lx, ly, ring), halfWidthOf(drag.inst.size), occupied(drag.inst.uid));
      showOrb(tToHangXY(t, ring, drag.inst.d / 2), drag.inst.d);
      drag.inst.orient.style.transform = `rotate(${restingDeg(t)}deg)`;
    } else {
      hideOrb();
      drag.inst.orient.style.transform = 'rotate(0deg)';
    }
  });

  window.addEventListener('pointerup', e => {
    if (!drag) return;
    const r = stage.getBoundingClientRect();
    const lx = e.clientX - r.left, ly = e.clientY - r.top;
    const { inst, fromT, moved, vx } = drag;
    if (drag.orientTo) gsap.killTweensOf(inst.orient); // stop the slot-chase; setOrient sets the final angle
    const overLibrary = pointInRect(e.clientX, e.clientY, library.getBoundingClientRect());
    const overTrayTrash = tray.style.opacity === '1' && pointInRect(e.clientX, e.clientY, trayTrash.getBoundingClientRect());

    hideOrb();
    inst.el.classList.remove('is-dragging');
    inst.el.style.zIndex = '10';
    drag = null; // clear before layout so the released charm is included in the tray

    if (!moved) {
      // A plain click never deletes (only the trash icon does). A placed charm was parked
      // by startDrag, so restore it to its spot; a tray charm just stays parked.
      if (fromT !== null && placeCharm(build, inst.uid, fromT)) setOrient(inst, orientForT(fromT));
    } else if (overLibrary || overTrayTrash) {
      removeInstance(inst.uid);                     // dragged onto the library or tray trash → delete
    } else if (BEADS) {
      // Snap to the nearest free pinned slot; reject (→ tray) if none in range.
      const slot = nearestFreeSlot(lx, ly, occupiedSlots(inst.uid), baseBox(), SLOT_SNAP_TOL, beadsCfg.slots);
      if (slot !== null && placeCharm(build, inst.uid, slot)) placeAtT(inst, slot, vx);
    } else if (distToRing(lx, ly, ring) < SNAP_TOLERANCE) {
      const occ = occupied(inst.uid);
      const hw = halfWidthOf(inst.size);
      let t = nudgeToFreeSlot(projectToRing(lx, ly, ring), hw, occ);
      if (!isSlotFree(t, hw, occ)) t = projectToRing(lx, ly, ring); // defensive (nudge is robust)
      if (placeCharm(build, inst.uid, t)) placeAtT(inst, t, vx);
      // if blocked by max, it falls through and is parked in the tray below.
    }
    // Any survivor not on the ring gets parked into the tray track (spawned/click/off-ring
    // drops). Per-charm hover trash is RING-ONLY (tray charms delete via the tray trash).
    if (instances.has(inst.uid)) {
      if (tOf(inst.uid) === null) parkToTray(inst);
      setTrashShown(inst, tOf(inst.uid) !== null);
    }
    layoutTray();
    renderBar();
  });

  function settle(body: HTMLElement, vx: number): void {
    gsap.killTweensOf(body);
    let kick = clampDeg(-vx * SNAP_KICK_GAIN);
    if (Math.abs(kick) < MIN_KICK) kick = (kick < 0 ? -1 : 1) * MIN_KICK;
    gsap.fromTo(body, { rotation: kick }, { rotation: 0, duration: 0.7, ease: 'elastic.out(1, 0.45)' });
  }

  // ── Boot ──────────────────────────────────────────────────────────────────
  renderLibrary();
  buildBar();
  // Filter panel (slide-in over the configurator). Re-renders the library on change;
  // its open button sits in the library top bar.
  const filters = mountFilters({
    catalog,
    mount: wrap,
    onChange: filtered => { visibleCharms = filtered; renderLibrary(); },
  });
  libBar.appendChild(filters.button);
  // Mount the right panel (Chains | Charms). The charm library + its drag-spawn
  // handler live here; ui.ts places the grid in the Charms tab and owns Chains.
  panelApi = mountPanel({
    panel, charmsLibrary: library, catalog, build, onChange: refreshChain,
    onTab: name => { currentTab = name; if (name === 'charms') charmsVisited = true; renderBar(); drawerApplyRingZoom(); },
  });
  // Rebuild DOM for a persisted build (e.g. returning from step 3 → "Edit design").
  // The charms already live in build.charms; recreate their instances and park the
  // unplaced ones. layoutRing (below) then positions the placed ones.
  for (const c of build.charms) {
    const item = catalog.charms.find(ci => ci.productId === c.productId);
    if (!item) continue;
    const inst = makeInstance(c.uid, item);
    if (c.t === null) parkToTray(inst);
  }
  if (build.charms.length) charmsVisited = true; // returning with a design → CTA stays "Continue to review"
  refreshClosure(); // composite the initially-selected closure
  renderBar();
  // Ring metrics need the stage laid out; do it next frame, and on resize.
  const calibrating = BEADS; // TEMP calibration: overlay shows on ANY beads base while the flags above are on
  const SLOTS_NAME = build.base === 'keychain' ? 'KEYCHAIN_SLOTS' : 'BAGCHAIN_SLOTS';
  const CLOSURES_NAME = build.base === 'keychain' ? 'KEYCHAIN_CLOSURE_SLOTS' : 'BAGCHAIN_CLOSURE_SLOTS';
  if (calibrating && BEADS_CALIBRATE) mountCalibration(beadsCfg.slots, SLOTS_NAME, 'Copy slots', 'top-3 right-3', 'bg-[#FFCA22] border-[#801431] text-[#801431]');
  if (calibrating && BEADS_CALIBRATE_CLOSURES) mountCalibration(beadsCfg.closures, CLOSURES_NAME, 'Copy closures', 'top-14 right-3', 'bg-[#2BB3C0] border-white text-white');
  requestAnimationFrame(layoutRing);
  window.addEventListener('resize', layoutRing, { signal });
  // Beads slot positions depend on the rendered base-image box, so re-layout once the
  // base PNG's natural dimensions are known (and whenever a palette swaps it in).
  if (BEADS) ringImg.addEventListener('load', () => layoutRing(), { signal });

  // Crossing the mobile/desktop breakpoint changes the whole layout model, so rebuild:
  // fade out, re-mount (picks up the new MOBILE), fade the fresh tree in.
  window.addEventListener('resize', () => {
    if (mobileMql() === MOBILE) return;        // same breakpoint → nothing structural to do
    wrap.style.transition = 'opacity 180ms ease';
    wrap.style.opacity = '0';
    window.setTimeout(() => {
      mountConfigurator(root, catalog, build, opts); // rebuilds for the new breakpoint
      const next = root.firstElementChild as HTMLElement | null;
      if (next) {
        next.style.opacity = '0';
        next.style.transition = 'opacity 180ms ease';
        requestAnimationFrame(() => requestAnimationFrame(() => { next.style.opacity = '1'; }));
      }
    }, 180);
  }, { signal });

  if (MOBILE) { applyHeaderOffset(); setupMobileDrawer(); }

  // The shell stays full-screen (100svh); we just pad the content down past the site
  // header (its height + 1rem of breathing room). The top bar is absolute, so it gets
  // the same top offset; the flow content clears the header + the bar.
  function applyHeaderOffset(): void {
    const hdr = document.querySelector('header');
    if (!hdr) return;
    const apply = () => {
      const top = (hdr as HTMLElement).offsetHeight + 24; // header height + 1rem + 1.5rem
      bar.style.top = top + 'px';
      wrap.style.paddingTop = (top + bar.offsetHeight + 16) + 'px';
    };
    requestAnimationFrame(apply);
    window.addEventListener('resize', apply, { signal });
  }

  // ── Mobile drawer (bottom sheet) ────────────────────────────────────────────
  // The panel sits at ~60svh, translated down to a peek; dragging the grabber raises
  // it. The tray rides just above the sheet's visible top so you see charms as you add
  // them; collapsing happens when you grab a charm or tap the canvas (handled above).
  function setupMobileDrawer(): void {
    let peek = DRAWER_PEEK; // px visible when collapsed (handle + tabs + a thin sliver; taller in review)
    let justDragged = false; // true briefly after a drag, to swallow the trailing click
    const handle = el('div', 'shrink-0 flex items-center justify-center pt-3 pb-3 touch-none cursor-grab');
    handle.innerHTML = '<span class="block w-10 h-1 rounded-full bg-foreground/25"></span>';
    panel.prepend(handle);
    tray.classList.add('z-[45]'); // ride above the sheet (stage has no stacking context on mobile)

    const sheetH = () => panel.offsetHeight || Math.round(window.innerHeight * 0.55);
    const collapsedY = () => Math.max(0, sheetH() - peek);

    const applyY = (y: number) => {
      panel.style.transform = `translateY(${y}px)`;
      tray.style.bottom = (sheetH() - y + 8) + 'px'; // tray sits just above the visible sheet
    };
    // Position the piece for the current drawer + tab state. Collapsed → reset. Expanded
    // on Charms → a small lift so the whole piece stays visible above the sheet. Expanded
    // on Chains → zoom into the top of the ring so the chain + closure read large
    // (~CHAIN_ZOOM_VW of the viewport), the closure parked at CHAIN_ZOOM_BIAS of the band.
    //
    // The transform-origin is ALWAYS the closure anchor (even for the lift/reset states,
    // which are pure translate/identity and so origin-independent). Keeping it constant is
    // what makes the zoom animate smoothly BOTH ways: if the origin changed at the same
    // time as the transform, the still-scaled matrix would snap to the new origin for one
    // frame (the "jumps up then zooms out" glitch) before the transition caught up.
    const applyRingTransform = (): void => {
      if (reviewMode) return;                 // review owns its own scale
      if (BEADS) {                            // beads: lift on most tabs; on the Chain tab, zoom into the first carabiner
        const box = baseBox();
        const lc = slotPx(beadsCfg.closures[0], box);            // first carabiner anchor (bag charm = left, keychain = top)
        pieceLayer.style.transformOrigin = `${lc.x}px ${lc.y}px`; // stable across states (mirrors the ring)
        pieceLayer.style.transition = 'transform 300ms ease';
        if (!drawerExpanded) { pieceLayer.style.transform = ''; return; }
        if (currentTab !== 'chain') { pieceLayer.style.transform = `translateY(${CHARMS_LIFT}%)`; return; }
        const band = stage.clientHeight - sheetH();               // visible canvas above the expanded sheet
        const scale = (window.innerWidth * CHAIN_ZOOM_VW) / (box.width * BEADS_ZOOM_REGION);
        const dx = (stage.clientWidth * BEADS_ZOOM_X) - lc.x;     // pull the left closure inward
        const dy = (band * BEADS_ZOOM_Y) - lc.y;                  // land it at BIAS of the band
        pieceLayer.style.transform = `translate(${dx}px, ${dy}px) scale(${scale})`;
        return;
      }
      const a = tToXY(0, ring);                                  // top-center anchor (where the closure sits)
      const py = a.y + ring.radius * CLOSURE_OFFSET;             // closure visual centre (y)
      pieceLayer.style.transformOrigin = `${a.x}px ${py}px`;     // stable across every state
      pieceLayer.style.transition = 'transform 300ms ease';
      if (!drawerExpanded) { pieceLayer.style.transform = ''; return; }
      if (currentTab !== 'chains') { pieceLayer.style.transform = `translateY(${CHARMS_LIFT}%)`; return; }
      const band = stage.clientHeight - sheetH();                // visible canvas above the expanded sheet
      const scale = (window.innerWidth * CHAIN_ZOOM_VW) / (ring.radius * 2 * RING_IMG_SCALE);
      const dy = (band * CHAIN_ZOOM_BIAS) - py;                  // land the closure centre at BIAS of the band
      pieceLayer.style.transform = `translate(0px, ${dy}px) scale(${scale})`;
    };
    drawerApplyRingZoom = applyRingTransform;
    const snap = (toExpanded: boolean) => {
      drawerExpanded = toExpanded;
      panel.style.transition = '';            // re-enable the class transition for the snap
      tray.style.transition = 'bottom 300ms ease';
      applyY(toExpanded ? 0 : collapsedY());
      applyRingTransform();                    // lift (Charms) or zoom-to-top (Chains); reset on collapse
    };
    collapseDrawer = () => { if (drawerExpanded || panel.style.transform === '') snap(false); };
    drawerSnap = snap;
    drawerSetPeek = (px: number) => { peek = px; snap(drawerExpanded); };

    // Tap the collapsed sheet anywhere → expand it (don't act on the tapped control yet).
    // In review the buttons live in the peek and must stay tappable, so don't intercept.
    // A click right after a drag is swallowed so it doesn't expand or switch tabs.
    panel.addEventListener('click', e => {
      if (justDragged) { e.preventDefault(); e.stopPropagation(); return; }
      if (!drawerExpanded && !reviewMode) { e.preventDefault(); e.stopPropagation(); snap(true); }
    }, true);

    requestAnimationFrame(() => snap(false)); // start collapsed
    window.addEventListener('resize', () => { applyY(drawerExpanded ? 0 : collapsedY()); applyRingTransform(); }, { signal });

    // Drag to raise / lower the sheet — from the grabber AND the tabs strip (a much
    // bigger target). A small move threshold lets taps on the tabs still switch tabs.
    const tabsEl = panel.querySelector<HTMLElement>('.cfg__panel-design');
    if (tabsEl) tabsEl.style.touchAction = 'none';
    const attachDrag = (zone: HTMLElement | null): void => {
      if (!zone) return;
      let startY = 0, base = 0, on = false, moved = false;
      zone.addEventListener('pointerdown', e => { startY = e.clientY; base = drawerExpanded ? 0 : collapsedY(); on = true; moved = false; });
      zone.addEventListener('pointermove', e => {
        if (!on) return;
        const dy = e.clientY - startY;
        if (!moved) {
          if (Math.abs(dy) < 6) return;        // below threshold → still a tap
          moved = true;
          panel.style.transition = 'none'; tray.style.transition = 'none';
          zone.setPointerCapture(e.pointerId);
        }
        applyY(Math.min(collapsedY(), Math.max(0, base + dy)));
      });
      const up = (e: PointerEvent): void => {
        if (!on) return; on = false;
        if (!moved) return;                    // a tap → let the click handlers run
        snap(base + (e.clientY - startY) < collapsedY() / 2); // past halfway → expand
        justDragged = true; window.setTimeout(() => { justDragged = false; }, 0);
      };
      zone.addEventListener('pointerup', up);
      zone.addEventListener('pointercancel', up);
    };
    drawerAttachDrag = attachDrag;
    attachDrag(handle);
    attachDrag(tabsEl);
  }
}

// Cross-fade an <img> to a new src WITHOUT the new asset popping in late. Sequence:
//   1. fade out (opacity 0)
//   2. wait FADE_MS for the fade-out to finish
//   3. swap the src ON THE REAL ELEMENT while fully invisible
//   4. wait for THAT element to decode the new bitmap (an <img> keeps showing its old frame
//      until the new src is decoded — decoding a throwaway Image() does NOT help here)
//   5. hold SWAP_SETTLE_MS, then fade back in
// so it's always fade out → change → fade in (never fade in → change). `my`/`latest()`
// form a generation guard: a newer selection bumps the counter, so an in-flight swap that
// resolves late just bails instead of fighting.
function crossfadeImage(img: HTMLImageElement, src: string, my: number, latest: () => number): void {
  img.style.opacity = '0';
  window.setTimeout(() => {
    if (my !== latest()) return;                                   // superseded during the fade-out
    img.src = src;                                                 // swap while invisible
    const fadeIn = (): void => {
      if (my !== latest()) return;                                 // superseded during decode
      window.setTimeout(() => { if (my === latest()) img.style.opacity = '1'; }, SWAP_SETTLE_MS);
    };
    if (img.decode) img.decode().then(fadeIn, fadeIn);             // resolves when THIS element is paint-ready
    else { img.onload = fadeIn; img.onerror = fadeIn; }
  }, FADE_MS);
}

function pointInRect(x: number, y: number, r: DOMRect): boolean {
  return x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;
}
